import type { Capability } from '@hendrx/authorization/constants';
import type { ParsedLocation } from '@tanstack/react-router';

import { getCookie } from 'shared/client/utils/cookies';
import { plenaryIdentity } from 'shared/client/utils/plenaryIdentity';
import { LOBBY_PATH, LOGIN_PATH, UNAUTHORIZED_PATH } from 'shared/common/consts/paths';
import { isFreeContentEmbedSignal } from 'shared/utils/freeContentEmbed';
import { getPlenaryActivityInformation } from 'shared/utils/plenary/getPlenaryActivityInformation';
import { getPlenarySignUpCookieName } from 'shared/utils/plenary/getPlenaryStudentCookieName';

import type { GuardContext, GuardResult, GuardRule, Role } from './createRouteGuard';

function getPlenarySignUpStatus(location: ParsedLocation) {
  if (typeof window === 'undefined') return { id: null, hasCookie: false };
  const url = new URL(location.pathname, window.location.origin);
  const activityInfo = getPlenaryActivityInformation(url);
  if (!activityInfo.id) return { id: null, hasCookie: false };
  const hasCookie = getCookie(getPlenarySignUpCookieName(activityInfo)) !== null || plenaryIdentity.contextId !== null;
  return { id: activityInfo.id, hasCookie };
}

export const STUDIO_ROLES = ['admin'] as const satisfies ReadonlyArray<Role>;

export const requireAuthenticated =
  ({ redirectToLogin = true }: { redirectToLogin?: boolean } = {}): GuardRule =>
  ({ auth, location }): GuardResult => {
    if (auth.isUser) return { allow: true };
    if (redirectToLogin) {
      return {
        allow: false,
        redirect: {
          to: LOGIN_PATH,
          search: { redirectTo: `${location.pathname}${location.searchStr ? `?${location.searchStr}` : ''}` }
        }
      };
    }
    return { allow: false, redirect: { to: UNAUTHORIZED_PATH } };
  };

export const requireRole =
  (...roles: Role[]): GuardRule =>
  ({ auth, location }): GuardResult => {
    if (!auth.isUser) {
      return {
        allow: false,
        redirect: {
          to: LOGIN_PATH,
          search: { redirectTo: `${location.pathname}${location.searchStr ? `?${location.searchStr}` : ''}` }
        }
      };
    }
    if (!auth.user || !roles.includes(auth.user.role)) {
      return { allow: false, redirect: { to: UNAUTHORIZED_PATH } };
    }
    return { allow: true };
  };

/**
 * Admits on a capability rather than a role.
 *
 * A **sibling** to `requireRole`, not a replacement — every other route keeps its role gate. It exists
 * because an external author is a `coordinator`, a role that grants nothing and must keep granting nothing:
 * their Studio access arrives as a stored grant scoped to their organization, so the gate has to ask what
 * the caller *may do* rather than what they *are*. Widening `requireRole` to include coordinators would hand
 * every CET coordinator authoring access instead.
 *
 * Denial is a redirect, exactly as `requireRole` denies — this decides which *surface* a caller may reach,
 * and is not the per-item content decision, which the server answers and which presents as not-found.
 *
 * The frontend gate is an affordance, never the boundary: the API and the collaboration server re-decide
 * every request from the principal, so a caller who edits `capabilities` in the browser reaches a surface
 * that returns them nothing.
 */
export const requireCapability =
  (capability: Capability): GuardRule =>
  ({ auth, location, permissions }): GuardResult => {
    if (!auth.isUser) {
      return {
        allow: false,
        redirect: {
          to: LOGIN_PATH,
          search: { redirectTo: `${location.pathname}${location.searchStr ? `?${location.searchStr}` : ''}` }
        }
      };
    }
    if (!permissions.capabilities.includes(capability)) {
      return { allow: false, redirect: { to: UNAUTHORIZED_PATH } };
    }
    return { allow: true };
  };

export const requirePlenaryCookie =
  (): GuardRule =>
  ({ location }): GuardResult => {
    const { id, hasCookie } = getPlenarySignUpStatus(location);
    if (!id || !hasCookie) return { allow: false, redirect: { to: UNAUTHORIZED_PATH } };
    return { allow: true };
  };

export const requirePlenaryLobbyAccess =
  (): GuardRule =>
  ({ auth, location }): GuardResult => {
    if (!auth.user || auth.user.role !== 'student') return { allow: true };
    const { hasCookie } = getPlenarySignUpStatus(location);
    if (hasCookie) return { allow: true };
    return { allow: false, redirect: { to: LOBBY_PATH } };
  };

/**
 * Keeps `mode=preview` on the route for a caller who may author it, and strips it for everyone else.
 *
 * Admits on **either** axis, and which axis a route passes is a scope statement, not a style choice:
 *
 *  - `{ capability: 'studio.access' }` — the learning-activity player, the route the Studio preview screen
 *    loads in an iframe. It has to ask the capability for the same reason `/studio` does: an external author
 *    is a `coordinator`, so a role list silently downgraded their preview to an ordinary player session, and
 *    the downgrade is invisible at the call site because the stripped `mode` lands on the iframe's URL, not
 *    the one the author is looking at. Every affordance keyed on `isStudioPreview` then answered as though the
 *    author were a learner. Identical for every existing role: `ROLE_CAPABILITIES` grants `studio.access` to
 *    admin and to nobody else.
 *  - `{ allowedRoles: STUDIO_ROLES }` — the PBL and course players, deliberately unchanged. A Darca grant does
 *    carry `studio.access` (it is what admits them to `/studio` at all), so capability-gating these would let
 *    their preview mode survive on two surfaces US4 trimmed out of scope. The server would answer those drafts
 *    404 on ownership, but "empty because the server refuses" is a weaker guarantee than "the frontend never
 *    offers it", and the second is the one this guard exists to make.
 *
 * The dual shape is transitional in the same way `ROLE_CAPABILITIES` is: it collapses when PBL and course move
 * to capabilities on their own terms, not as a side effect of this feature.
 */
export const requirePreviewModeAllowed =
  (admits: { allowedRoles: ReadonlyArray<Role> } | { capability: Capability }): GuardRule =>
  ({ auth, location, permissions }): GuardResult => {
    const search = location.search as Record<string, unknown>;
    if (search.mode !== 'preview') return { allow: true };
    const isAdmitted =
      'capability' in admits
        ? permissions.capabilities.includes(admits.capability)
        : !!auth.user && admits.allowedRoles.includes(auth.user.role);
    if (isAdmitted) return { allow: true };
    const newSearch = { ...search };
    delete newSearch.mode;
    return { allow: false, redirect: { to: location.pathname, search: newSearch } };
  };

/**
 * Allows a request that carries the free-content embed signal. The frontend cannot know an item's
 * `businessDomain`, so it only relaxes the login redirect on the embed signal — the server
 * (`/authorization` + the content guard) stays authoritative and still denies non-720 anonymous access,
 * so relaxing the FE gate never itself grants access to protected content. Uses the canonical
 * `isFreeContentEmbedSignal` (slxapi OR `host=infinity`), matching the sibling hooks: `useContentSessionExchange`
 * strips `slxapi` from the URL after the token exchange, leaving only `host=infinity`, so a slxapi-only
 * gate would redirect a valid exchanged session to login on a param-less reload. Compose via
 * `anyOf(requireAuthenticated(), allowFreeContentEmbed())` so an authenticated user still takes the normal
 * path and a plain anonymous user (no signal) still gets the login redirect.
 */
export const allowFreeContentEmbed =
  (): GuardRule =>
  ({ location }): GuardResult => {
    const search = location.search as { slxapi?: unknown; host?: unknown };
    if (isFreeContentEmbedSignal(search)) return { allow: true };
    return { allow: false, redirect: { to: UNAUTHORIZED_PATH } };
  };

export const anyOf =
  (...rules: GuardRule[]): GuardRule =>
  async (ctx: GuardContext): Promise<GuardResult> => {
    let firstDenial: (GuardResult & { allow: false }) | null = null;
    for (const rule of rules) {
      const result = await rule(ctx);
      if (result.allow) return result;
      if (!firstDenial) firstDenial = result as GuardResult & { allow: false };
    }
    return firstDenial ?? { allow: false, redirect: { to: UNAUTHORIZED_PATH } };
  };
